/**
 * Centralized API Client for all signup forms
 * 
 * This single shared script handles:
 * 1. GeoLocation API - fetch visitor's country and IP
 * 2. Lead API - submit signup forms with correct payload and response handling
 * 
 * All forms MUST use this shared API client - no duplicate API calls in components.
 */

const API_BASE_URL = 'https://leadapi.crmbytes.com/api';
const GEO_API_URL = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object>} Geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEO_API_URL, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch geolocation');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Geolocation API error:', error);
    return null;
  }
};

/**
 * Map country codes to dial codes
 * @param {string} countryCode - ISO country code (e.g., 'PK', 'US')
 * @returns {string} Dial code (e.g., '+92', '+1')
 */
const getDialCodeFromCountry = (countryCode) => {
  const countryToDialCode = {
    'US': '+1', 'CA': '+1', 'GB': '+44', 'IN': '+91', 'AU': '+61',
    'PK': '+92', 'CN': '+86', 'JP': '+81', 'DE': '+49', 'FR': '+33',
    'IT': '+39', 'ES': '+34', 'MX': '+52', 'BR': '+55', 'ZA': '+27',
    'AE': '+971', 'SA': '+966', 'EG': '+20', 'NG': '+234', 'KE': '+254',
    'RU': '+7', 'TR': '+90', 'KR': '+82', 'TH': '+66', 'MY': '+60',
    'SG': '+65', 'PH': '+63', 'ID': '+62', 'VN': '+84', 'BD': '+880',
    'NL': '+31', 'BE': '+32', 'SE': '+46', 'NO': '+47', 'DK': '+45',
    'FI': '+358', 'PL': '+48', 'GR': '+30', 'PT': '+351', 'CZ': '+420',
    'AT': '+43', 'CH': '+41', 'IE': '+353', 'NZ': '+64', 'AR': '+54',
    'CL': '+56', 'CO': '+57', 'PE': '+51', 'VE': '+58', 'IL': '+972'
  };
  
  return countryToDialCode[countryCode] || '+1'; // Default to +1 if not found
};

/**
 * Get geolocation info (exported for form display)
 * @returns {Promise<Object>} Full geolocation response
 */
export const getGeoLocation = async () => {
  try {
    const geoData = await fetchGeolocation();
    return geoData;
  } catch (error) {
    console.error('Error fetching geolocation:', error);
    return null;
  }
};

/**
 * Get country information from geolocation for dial code auto-selection
 * @returns {Promise<Object>} Country info { countryName, countryCode, dialCode, ipAddress }
 */
export const getCountryInfo = async () => {
  try {
    const geoData = await fetchGeolocation();
    if (geoData && geoData.isSuccess && geoData.data) {
      const countryCode = geoData.data.countryCode || '';
      return {
        countryName: geoData.data.countryName || '',
        countryCode: countryCode,
        dialCode: getDialCodeFromCountry(countryCode),
        ipAddress: geoData.data.ipAddress || '',
        cityName: geoData.data.cityName || '',
        regionName: geoData.data.regionName || ''
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching country info:', error);
    return null;
  }
};

/**
 * Submit lead form data
 * @param {Object} formData - Form data to submit
 * @param {Object} geoData - Geolocation data
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (formData, geoData = null) => {
  try {
    // Extract phone number - remove all non-numeric characters
    const cleanPhoneNumber = formData.phone.replace(/\D/g, '');
    
    // Get country dial code (numeric only, no +)
    const countryDialCode = formData.countryCode ? 
      parseInt(formData.countryCode.replace('+', '')) : 1;
    
    // Use countryCode directly from geolocation API (e.g., "US", "PK", "NL")
    // NOT countryName (e.g., "United States", "Pakistan", "Netherlands")
    const country = geoData?.data?.countryCode || 'US';

    // Build required fields
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: countryDialCode,
      country: country,
      phoneNumber: cleanPhoneNumber,
      ipAddress: geoData?.data?.ipAddress || '',
      description: formData.message || formData.brandName || 'Trademark registration inquiry',
      pageUrl: window.location.href
    };

    // Add optional fields only if they exist (don't send empty strings)
    if (geoData?.data?.cityName) {
      payload.city = geoData.data.cityName;
    }
    if (geoData?.data?.regionName) {
      payload.state = geoData.data.regionName;
    }
    if (geoData) {
      payload.geoLocation = JSON.stringify(geoData);
    }
    if (formData.packageTitle) {
      payload.packageTitle = formData.packageTitle;
    }
    if (formData.packageAmount) {
      payload.packageAmount = Number(formData.packageAmount) || 0;
    }

    const response = await fetch(`${API_BASE_URL}/Lead/AddLead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Return standardized response
    return {
      success: response.ok && data.isSuccess,
      statusCode: data.statusCode || response.status,
      data: data.data || null,
      errorMessage: data.errorMessage || data.message || null,
      customerId: data.data?.customerId || null
    };
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      statusCode: 500,
      errorMessage: 'Network error. Please try again.',
      data: null,
      customerId: null
    };
  }
};

/**
 * Handle form submission with geolocation and lead API
 * @param {Object} formData - Form data
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 */
export const handleFormSubmission = async (formData, onSuccess, onError) => {
  try {
    // Step 1: Fetch geolocation
    const geoData = await fetchGeolocation();
    
    // Step 2: Submit lead
    const result = await submitLead(formData, geoData);
    
    // Step 3: Handle response based on status code
    if (result.statusCode === 200 && result.data?.customerId) {
      // Success - redirect to application page
      if (onSuccess) {
        onSuccess(result);
      }
      // Redirect immediately
      window.location.href = `/application/main?customerid=${result.data.customerId}`;
    } else if (result.statusCode === 401) {
      // Unauthorized - show message field
      if (onError) {
        onError(result.errorMessage || 'Unauthorized access');
      }
    } else if (result.statusCode === 400) {
      // Bad request - show errorMessage field
      if (onError) {
        onError(result.errorMessage || 'Invalid form data');
      }
    } else {
      // Other errors
      if (onError) {
        onError(result.errorMessage || 'Something went wrong. Please try again.');
      }
    }
  } catch (error) {
    console.error('Form submission error:', error);
    if (onError) {
      onError('Failed to submit form. Please try again.');
    }
  }
};
